import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

export interface ExportColumn {
  title: string;
  /** Maps a row to the cell value for this column */
  value: (row: any) => string | number;
}

export interface PharmacyHeader {
  name: string;
  addressLine?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  drugLicenseNumber?: string;
}

export const formatCurrency = (v: number | undefined | null): string =>
  `₹${(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** jsPDF built-in fonts can't render the ₹ glyph — use "Rs." in PDFs. */
export const pdfCurrency = (v: number | undefined | null): string =>
  `Rs. ${(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const sanitizeFilename = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const csvEscape = (value: string | number): string => {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export function exportCsv(
  title: string,
  columns: ExportColumn[],
  rows: any[],
  extraLines: string[] = []
): void {
  const lines: string[] = [];
  lines.push(csvEscape(title));
  extraLines.forEach((l) => lines.push(csvEscape(l)));
  if (extraLines.length || title) lines.push("");
  lines.push(columns.map((c) => csvEscape(c.title)).join(","));
  rows.forEach((row) => {
    lines.push(columns.map((c) => csvEscape(c.value(row))).join(","));
  });

  // UTF-8 BOM so Excel renders ₹ and other non-ASCII correctly
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitizeFilename(title)}-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface PdfExportOptions {
  title: string;
  pharmacy: PharmacyHeader;
  /** e.g. "01/08/2026 – 09/08/2026" or filter description */
  subtitle?: string;
  columns: ExportColumn[];
  rows: any[];
  /** Label/value pairs printed above the table */
  summary?: Array<{ label: string; value: string }>;
  orientation?: "portrait" | "landscape";
}

export function exportPdf(options: PdfExportOptions): void {
  const {
    title,
    pharmacy,
    subtitle,
    columns,
    rows,
    summary = [],
    orientation = "portrait",
  } = options;

  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = 40;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(pharmacy.name || "Pharmacy", pageWidth / 2, y, {
    align: "center",
  });
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const headerLines: string[] = [];
  if (pharmacy.addressLine) headerLines.push(pharmacy.addressLine);
  const contact = [
    pharmacy.phone ? `Ph: ${pharmacy.phone}` : "",
    pharmacy.email ? `Email: ${pharmacy.email}` : "",
  ]
    .filter(Boolean)
    .join("  |  ");
  if (contact) headerLines.push(contact);
  const legal = [
    pharmacy.gstin ? `GSTIN: ${pharmacy.gstin}` : "",
    pharmacy.drugLicenseNumber ? `DL No: ${pharmacy.drugLicenseNumber}` : "",
  ]
    .filter(Boolean)
    .join("  |  ");
  if (legal) headerLines.push(legal);

  headerLines.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 12;
  });

  y += 6;
  doc.setDrawColor(180);
  doc.line(margin, y, pageWidth - margin, y);
  y += 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    `Generated: ${dayjs().format("DD/MM/YYYY HH:mm")}`,
    pageWidth - margin,
    y,
    { align: "right" }
  );
  y += 14;

  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(subtitle, margin, y);
    doc.setTextColor(0);
    y += 14;
  }

  if (summary.length) {
    const parts = summary.map((s) => `${s.label}: ${s.value}`);
    const text = doc.splitTextToSize(
      parts.join("    |    "),
      pageWidth - margin * 2
    );
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(text, margin, y);
    doc.setFont("helvetica", "normal");
    y += text.length * 12 + 6;
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [columns.map((c) => c.title)],
    body: rows.map((row) => columns.map((c) => String(c.value(row) ?? ""))),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [24, 119, 242], fontSize: 8 },
    alternateRowStyles: { fillColor: [246, 248, 250] },
    didDrawPage: () => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(
        `Page ${doc.getNumberOfPages()}`,
        pageWidth - margin,
        pageHeight - 16,
        { align: "right" }
      );
      doc.setTextColor(0);
    },
  });

  doc.save(`${sanitizeFilename(title)}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
}
